import { cn, formatPhoneNumber } from 'lib/helpers/utility';

import QrCode from 'components/shared/QrCode/QrCode';
import DocText from 'components/shared/DocumentSheet/DocText';
import { DOC_GAPS, DOC_TEXT } from 'components/shared/DocumentSheet/lib/docStyles';
import DocSectionHead from 'components/shared/DocumentSheet/components/DocSectionHead';

const CMS_URL = 'www.cms.gov/nosurprises';
const CMS_PHONE = '1-800-985-3059';

const Strong = ({ children }) => <span className="font-700 text-neutral-900">{children}</span>;

const DISCLOSURES = [
  {
    title: 'This is only an estimate.',
    body: 'Actual items, services, and charges may differ. For recurring care this estimate is valid for no longer than 12 months; a new one is issued at each visit.'
  },
  {
    title: 'Additional recommended services.',
    body: 'Your provider may recommend items or services as part of your course of care that are not reflected here and must be scheduled or requested separately.'
  },
  {
    title: 'Your right to dispute.',
    dispute: true
  },
  {
    title: 'This is not a contract.',
    body: 'This Good Faith Estimate does not obligate you to obtain any of the items or services from the providers or facilities identified here.'
  }
];

const Disclosure = ({ index, title, body, dispute }) => (
  <div className="flex gap-x-3">
    <div className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-md border border-solid border-primary-600">
      <DocText size="xs" className="w-auto text-[11px] font-600 leading-4 text-primary-600">
        {index}
      </DocText>
    </div>
    <DocText size="xs" className={DOC_TEXT.bodyMuted}>
      <Strong>{title}</Strong>{' '}
      {dispute ? (
        <>
          If billed <Strong>$400 or more</Strong> above this estimate, you may initiate
          patient–provider dispute resolution within <Strong>120 days</Strong> at {CMS_URL} or{' '}
          {CMS_PHONE}. Disputing will not affect the quality of your care.
        </>
      ) : (
        body
      )}
    </DocText>
  </div>
);

export const GfeDisclosures = () => (
  <div className={DOC_GAPS.sectionWide}>
    <DocSectionHead title="Required disclosures & patient rights" className="mb-1.5" />
    <DocText size="xs" className={DOC_TEXT.meta}>
      Issued under the No Surprises Act (45 C.F.R. §149.610).
    </DocText>

    <div className="mt-[14px] flex flex-col gap-y-[14px]">
      {DISCLOSURES.map((disclosure, index) => (
        <Disclosure key={disclosure.title} index={index + 1} {...disclosure} />
      ))}
    </div>
  </div>
);

export const GfeAcknowledgment = ({ practice }) => (
  <div className={DOC_GAPS.sectionWide}>
    <div className="flex items-center gap-x-[18px] rounded-lg border border-solid border-neutral-300 px-[18px] py-3.5">
      <div className="min-w-0 flex-1">
        <DocText
          variant="label"
          size="small"
          className="text-[11px] font-800 uppercase leading-4 tracking-[0.08em] text-neutral-900">
          FULL DISCLOSURES & DISPUTE INSTRUCTIONS
        </DocText>
        <DocText size="xs" className={cn(DOC_TEXT.bodyMuted, 'mt-1.5')}>
          Scan the code or visit the federal site for complete CMS disclosures and step-by-step
          dispute-resolution instructions referenced in item 3.
          <br />
          Online: <Strong>{CMS_URL}</Strong> · Phone: <Strong>{CMS_PHONE}</Strong>
        </DocText>
      </div>
      <div className="shrink-0">
        <QrCode value={`https://${CMS_URL}`} size={72} />
      </div>
    </div>

    <DocSectionHead title="Patient acknowledgment" className={cn(DOC_GAPS.sectionWide, 'mb-2.5')} />
    <DocText size="xs" className={DOC_TEXT.bodyMuted}>
      By completing your digital check-in you acknowledge <Strong>receipt and understanding</Strong>{' '}
      of this Good Faith Estimate. Upon completion, your{' '}
      <Strong>electronic signature and a date-and-time stamp</Strong> are affixed to your attached
      copy of this estimate.
    </DocText>

    <div className="mt-9 grid grid-cols-2 gap-x-12 xs:grid-cols-1 xs:gap-y-9">
      {[{ label: 'Patient / representative signature' }, { label: 'Date & time' }].map((field) => (
        <div key={field.label}>
          <div className="h-px bg-neutral-900" />
          <div className="mt-1.5 flex justify-between gap-x-3">
            <DocText variant="label" size="small" className={cn(DOC_TEXT.microLabel, 'w-auto')}>
              {field.label.toUpperCase()}
            </DocText>
            <DocText
              size="xs"
              className="w-auto whitespace-nowrap text-xxs italic leading-[14px] text-neutral-500">
              Affixed at digital check-in
            </DocText>
          </div>
        </div>
      ))}
    </div>

    <DocText size="xs" className={cn(DOC_TEXT.meta, 'mt-5')}>
      <Strong>Accessibility & recordkeeping.</Strong> This estimate is provided in clear language
      and is available in large print or an alternate language on request. A copy is retained in
      your medical record; you may request it on paper or electronically at any time
      {practice?.phone ? ` — call ${formatPhoneNumber(practice.phone)}` : ''}.
    </DocText>
  </div>
);

export default GfeDisclosures;
